// Best-effort Zillow listing URL parser.
// Extracts street address, city, state, and zip from the URL slug.
// Returns null if the URL isn't a recognized Zillow listing page.

const STREET_TYPES = new Set([
  'st', 'ave', 'blvd', 'dr', 'rd', 'ln', 'ct', 'pl', 'way', 'ter',
  'cir', 'trl', 'pkwy', 'hwy', 'loop', 'run', 'pass', 'xing', 'path',
  'walk', 'row', 'cv', 'cove', 'pt', 'trce', 'holw', 'mnr',
]);

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export interface ParsedZillowAddress {
  address: string;
  city: string;
  state: string;
  zip: string;
}

export function parseZillowUrl(url: string): ParsedZillowAddress | null {
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    if (!u.hostname.includes('zillow.com')) return null;

    // Matches /homedetails/{slug}/{zpid}_zpid/
    const match = u.pathname.match(/\/homedetails\/([^/]+)\//);
    if (!match) return null;

    const parts = match[1].split('-').filter(Boolean);

    // Find the zip: last 5-digit segment
    let zipIdx = -1;
    for (let i = parts.length - 1; i >= 0; i--) {
      if (/^\d{5}$/.test(parts[i])) { zipIdx = i; break; }
    }
    if (zipIdx < 2) return null; // need at least: number streetname state zip

    const zip = parts[zipIdx];

    // State is the segment immediately before zip
    const stateRaw = parts[zipIdx - 1];
    if (!/^[a-zA-Z]{2}$/.test(stateRaw)) return null;
    const state = stateRaw.toUpperCase();

    // Everything before state is the combined "street address + city" portion
    const addrParts = parts.slice(0, zipIdx - 1);
    if (addrParts.length === 0) return null;

    // Find the last known street type to split street address from city.
    // We scan from the end so multi-word cities (Eden Prairie, Apple Valley) work
    // as long as the street type appears before them.
    let streetTypeIdx = -1;
    for (let i = addrParts.length - 1; i >= 1; i--) {
      if (STREET_TYPES.has(addrParts[i].toLowerCase())) {
        streetTypeIdx = i;
        break;
      }
    }

    let address: string;
    let city: string;

    if (streetTypeIdx > 0 && streetTypeIdx < addrParts.length - 1) {
      address = addrParts.slice(0, streetTypeIdx + 1).map(cap).join(' ');
      city    = addrParts.slice(streetTypeIdx + 1).map(cap).join(' ');
    } else {
      // Can't split cleanly — give everything to address; city stays blank
      address = addrParts.map(cap).join(' ');
      city    = '';
    }

    return { address, city, state, zip };
  } catch {
    return null;
  }
}
